import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, X, Image } from "lucide-react";
import { toast } from "sonner";

interface Destination {
  id: string;
  title: string;
  slug: string;
  main_image_url: string | null;
  gallery: string[];
  short_description: string;
  long_description: string;
  features: string[];
  is_active: boolean;
  sort_order: number;
}

const emptyForm = {
  title: "",
  slug: "",
  main_image_url: "",
  gallery: [] as string[],
  short_description: "",
  long_description: "",
  features: [] as string[],
  is_active: true,
  sort_order: 0,
};

function slugify(text: string) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

export function DestinationsSection() {
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [newTag, setNewTag] = useState("");
  const [newGalleryUrl, setNewGalleryUrl] = useState("");

  const fetchData = async () => {
    const { data } = await supabase.from("destinations").select("*").order("sort_order");
    setDestinations((data as any[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (d: Destination) => {
    setEditing(d.id);
    setForm({
      title: d.title,
      slug: d.slug,
      main_image_url: d.main_image_url ?? "",
      gallery: d.gallery ?? [],
      short_description: d.short_description ?? "",
      long_description: d.long_description ?? "",
      features: d.features ?? [],
      is_active: d.is_active,
      sort_order: d.sort_order,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.slug) {
      toast.error("Título y slug son requeridos");
      return;
    }
    const payload = {
      title: form.title,
      slug: form.slug,
      main_image_url: form.main_image_url || null,
      gallery: form.gallery,
      short_description: form.short_description,
      long_description: form.long_description,
      features: form.features,
      is_active: form.is_active,
      sort_order: form.sort_order,
    };

    if (editing) {
      const { error } = await supabase.from("destinations").update(payload as any).eq("id", editing);
      if (error) { toast.error(error.message); return; }
      toast.success("Destino actualizado");
    } else {
      const { error } = await supabase.from("destinations").insert(payload as any);
      if (error) { toast.error(error.message); return; }
      toast.success("Destino creado");
    }
    setDialogOpen(false);
    fetchData();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("¿Eliminar este destino?")) return;
    await supabase.from("destinations").delete().eq("id", id);
    toast.success("Eliminado");
    fetchData();
  };

  const addTag = () => {
    if (newTag.trim()) {
      setForm({ ...form, features: [...form.features, newTag.trim()] });
      setNewTag("");
    }
  };

  const removeTag = (idx: number) => {
    setForm({ ...form, features: form.features.filter((_, i) => i !== idx) });
  };

  const addGalleryUrl = () => {
    if (newGalleryUrl.trim()) {
      setForm({ ...form, gallery: [...form.gallery, newGalleryUrl.trim()] });
      setNewGalleryUrl("");
    }
  };

  const removeGalleryUrl = (idx: number) => {
    setForm({ ...form, gallery: form.gallery.filter((_, i) => i !== idx) });
  };

  if (loading) return <p>Cargando...</p>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Destinos</h2>
        <Button onClick={openCreate}><Plus className="h-4 w-4 mr-1" /> Nuevo Destino</Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Imagen</TableHead>
            <TableHead>Título</TableHead>
            <TableHead>Slug</TableHead>
            <TableHead>Activo</TableHead>
            <TableHead>Orden</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {destinations.map((d) => (
            <TableRow key={d.id}>
              <TableCell>
                {d.main_image_url ? (
                  <img src={d.main_image_url} className="h-10 w-14 object-cover rounded" />
                ) : (
                  <div className="h-10 w-14 bg-muted rounded flex items-center justify-center"><Image className="h-4 w-4 text-muted-foreground" /></div>
                )}
              </TableCell>
              <TableCell className="font-medium">{d.title}</TableCell>
              <TableCell className="text-muted-foreground">/{d.slug}</TableCell>
              <TableCell>{d.is_active ? "✅" : "❌"}</TableCell>
              <TableCell>{d.sort_order}</TableCell>
              <TableCell className="text-right space-x-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(d)}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => handleDelete(d.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </TableCell>
            </TableRow>
          ))}
          {destinations.length === 0 && (
            <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Sin destinos</TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Destino" : "Nuevo Destino"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Título</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: editing ? form.slug : slugify(e.target.value) })} />
              </div>
              <div>
                <Label>Slug</Label>
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
              </div>
            </div>

            <div>
              <Label>Imagen Principal (URL)</Label>
              <Input value={form.main_image_url} onChange={(e) => setForm({ ...form, main_image_url: e.target.value })} placeholder="https://..." />
            </div>

            <div>
              <Label>Galería de Imágenes</Label>
              <div className="flex gap-2 mb-2">
                <Input value={newGalleryUrl} onChange={(e) => setNewGalleryUrl(e.target.value)} placeholder="URL de imagen" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addGalleryUrl())} />
                <Button type="button" size="sm" onClick={addGalleryUrl}>Agregar</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.gallery.map((url, i) => (
                  <div key={i} className="relative group">
                    <img src={url} className="h-16 w-20 object-cover rounded border" />
                    <button onClick={() => removeGalleryUrl(i)} className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full h-4 w-4 flex items-center justify-center text-xs">×</button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Descripción Corta</Label>
              <Textarea rows={2} value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} />
            </div>

            <div>
              <Label>Descripción Larga</Label>
              <Textarea rows={5} value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} />
            </div>

            <div>
              <Label>Características</Label>
              <div className="flex gap-2 mb-2">
                <Input value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="Ej: Playa, Montaña..." onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())} />
                <Button type="button" size="sm" onClick={addTag}>Agregar</Button>
              </div>
              <div className="flex flex-wrap gap-1">
                {form.features.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-secondary text-secondary-foreground px-2 py-0.5 rounded text-sm">
                    {f} <X className="h-3 w-3 cursor-pointer" onClick={() => removeTag(i)} />
                  </span>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Orden</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <Label>Activo</Label>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave}>{editing ? "Guardar" : "Crear"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
